import { formatItem, formatTypes, getFormattedDate } from 'global/common/dateFormatting'
import { gEnums } from 'global/enums/globalEnums'
import MotionWrapper from 'global/motions/MotionWrapper'
import Wrapper, { wrapperTypes } from 'global/wrappers/Wrapper'
import _ from 'lodash'
import { SportsSeasonContext } from 'projectSpecific/sports/cnr/contexts/SportsSeasonContext'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Icon } from 'semantic-ui-react'
import SectionSchedule from '../matches/SectionSchedule'
import PlayoffSchedule from '../matches/PlayoffSchedule'
import { LevelHeader } from './SectionHeader'
import { _matchLsTypes } from 'projectSpecific/sports/cnr/reducers/SportsDataReducer'
import { GotScore } from './GotScore'
import CardSponsor from 'global/components/sem/cards/CardSponsor'
import { _sportSponsorTypes } from 'projectSpecific/sports/useHelpers/useSportsSponsor'

const _applyTestDate = false
const _selectedDate = '09/04/2025'

export const Scoreboard = () => {

  const sportsSeasonContext = useContext(SportsSeasonContext);
  const { sportsSeason_state } = sportsSeasonContext ?? {};
  const { details_info, matches_info } = sportsSeason_state ?? {};
  const { matches_groupInfo } = matches_info ?? {};
  const { scheduleLevelKeys, scoreboardByDate, playoffScoreboardByDate } = matches_groupInfo ?? {};
  const { details } = details_info ?? {}
  const { showGotScores } = details ?? {}

  const today = new Date();
  const tdy = formatItem(formatTypes.shortestDate, today)

  const scoreboardDates = useMemo(() => {
    return _.sortBy(
      _.uniq([
        tdy,
        ..._.keys(scoreboardByDate)
      ]),
      (date) => new Date(date)
    );
  }, [scoreboardByDate])

  const [selectedDate, setSelectedDate] = useState()
  const [scoreboardDate, setScoreboardDate] = useState(_applyTestDate ? _selectedDate : selectedDate)

  const allowGotScore = tdy === scoreboardDate

  useEffect(() => {
    const _dateFormatted = getFormattedDate(new Date())
    setSelectedDate(_dateFormatted)
    setScoreboardDate(_dateFormatted)
  }, []);

  const msdts = gEnums.matchScheduleDisplayTypes
  const props = { matchScheduleDisplayType: msdts.scoreboard }

  const _dateCaption = formatItem(formatTypes.fullDate, scoreboardDate)

  const handleDateClick = (forward) => {

    if (!scoreboardDate || !scoreboardDates?.length) return;

    const currentIndex = _.indexOf(scoreboardDates, scoreboardDate);

    if (currentIndex === -1) return;

    const newIndex = forward
      ? (currentIndex + 1) % scoreboardDates.length
      : (currentIndex - 1 + scoreboardDates.length) % scoreboardDates.length;

    setScoreboardDate(scoreboardDates[newIndex]);

    // if (!scoreboardDate) return;

    // const [month, day, year] = scoreboardDate.split('/').map(Number);
    // const currentDate = new Date(year, month - 1, day);

    // currentDate.setDate(currentDate.getDate() + (forward ? 1 : -1));

    // const _newDate = currentDate.toLocaleDateString('en-US', {
    //   year: 'numeric',
    //   month: '2-digit',
    //   day: '2-digit'
    // });

    // if (_newDate) {
    //   setScoreboardDate(_newDate);
    // }
  };

  const handleTodayClick = () => setScoreboardDate(getFormattedDate(new Date()))

  const topDatesHeader = useMemo(() => {
    return <div className='date-header'>
      <div><Icon name='arrow left' onClick={() => { handleDateClick(false) }} /></div>
      <div>{_dateCaption}</div>
      <div>
        <Icon name='calendar check outline' onClick={() => { handleTodayClick(true) }} />
        <Icon name='arrow right' onClick={() => { handleDateClick(true) }} />
      </div>
    </div>
  }, [_dateCaption])

  const levelDateMatches = (levelKey, matchesBySection) => {

    if (!_.isEmpty(matchesBySection)) {

      const _matchesBySection = _.fromPairs(
        _.sortBy(_.toPairs(matchesBySection), ([key]) => {
          const lower = _.toLower(key);
          const isNon = _.startsWith(lower, 'non');
          return [isNon ? 1 : 0, lower];
        })
      );

      const sms = _.map(_matchesBySection, (sectionMatches, sectionKey) => {
        return <SectionSchedule
          dateKey={scoreboardDate}
          levelKey={levelKey}
          sectionKey={sectionKey}
          matches_sections={sectionMatches}
        />
      })
      return sms
    }
    return <div className='match-wrapper'>No Matches</div>
  }

  const levelDatePlayoffMatches = (levelKey, levelPlayoffMatches) => {
    if (!_.isEmpty(levelPlayoffMatches)) {
      return <PlayoffSchedule
        dateKey={scoreboardDate}
        levelKey={levelKey}
        matches_levels={levelPlayoffMatches}
      />
    }
    return <div className='match-wrapper'>No Playoff Matches</div>
  }

  const scoreboard = useMemo(() => {

    const sb = scoreboardByDate?.[scoreboardDate]

    const sortedLevelKeys = _.sortBy(scheduleLevelKeys, levelKey => {
      const sbl = sb?.[levelKey];
      return _.isEmpty(sbl) ? 1 : 0;
    });

    let _index = 0

    const scoreboards = _.map(sortedLevelKeys, levelKey => {
      const sbl = sb?.[levelKey]
      const levelHeader = <LevelHeader levelKey={levelKey} />
      _index++
      return <div className='scoreboard'>
        {levelHeader}
        {levelDateMatches(levelKey, sbl)}
      </div>
    })

    scoreboards.splice(1, 0, <CardSponsor
      sportSponsorType={_sportSponsorTypes.scoreboard}

    />)
    return scoreboards
  }, [scheduleLevelKeys, scoreboardByDate, scoreboardDate])


  const { playoffScoreboard, hasPlayoffSb } = useMemo(() => {
    const _scheduleLevelKeys = _.without(scheduleLevelKeys, 'Non', 'nonDistrict')
    const sb = playoffScoreboardByDate?.[scoreboardDate]

    const scoreboards = _.map(_scheduleLevelKeys, levelKey => {
      const sbl = sb?.[levelKey]
      const levelHeader = <LevelHeader levelKey={levelKey} />
      return <div className='scoreboard'>
        {levelHeader}
        {levelDatePlayoffMatches(levelKey, sbl)}
      </div>
    })
    return { playoffScoreboard: scoreboards, hasPlayoffSb: _.size(sb) > 0 ? true : false }
  }, [scheduleLevelKeys, playoffScoreboardByDate, scoreboardDate])

  return <Wrapper
    header={topDatesHeader}
    content={<MotionWrapper wrapperKey={`scoreboard-${scoreboardDate}`}>
      {showGotScores && allowGotScore && <GotScore />}
      <div className='scoreboard-wrapper'>
        {!hasPlayoffSb && scoreboard}
        {hasPlayoffSb && playoffScoreboard}
      </div>
    </MotionWrapper>}
    wrapperType={wrapperTypes.paddedHeader}
  ></Wrapper>

}
